import { Component, OnDestroy, ViewChild } from '@angular/core';
import { Store } from "@ngrx/store";
import * as fromRoot from "../../../../../../state/app.state";
import { FetchClinicListRequest } from '../../../core/clients-list.actions';
import { clinicsList } from "../../../core/clients-list.selectors";
import { Subject, takeUntil } from "rxjs";
import {
  clientStatusColors,
  indicatorColors,
  roles,
  sortOrderMap,
  statusLabelMap
} from '../../../../../shared/constants/common.constants';
import { ClinicsList } from '../../../../../../models/clinics-list';
import { CommonService } from '../../../../../shared/services/common.service';
import { ClientListService } from '../../../services/client-list.service';
import { Clinic } from '../../../../../../models/clinic';
import { Router } from '@angular/router';
import { MatSort, Sort } from "@angular/material/sort";
import { drillDownUrls } from "../../../../../shared/core/shared.selectors";
import { SetDrillDownUrls } from "../../../../../shared/core/shared.actions";
import { MatTable, MatTableDataSource } from "@angular/material/table";

@Component({
  selector: 'app-clinic-information-table',
  templateUrl: './clinic-information-table.component.html',
  styleUrls: ['./clinic-information-table.component.css']
})
export class ClinicInformationTableComponent implements OnDestroy {
  @ViewChild(MatTable) table: MatTable<any>;
  @ViewChild(MatSort) sort: MatSort;

  loading: boolean = false;
  tableData: any[];
  dataSource: any = new MatTableDataSource();
  role: number;
  roles = roles;
  isFirstPage = true;
  selectedUserId: number | null;
  selectedUserRole: number | null;
  clinicListDetails: ClinicsList;
  displayedColumns = [
    'name',
    'status',
    'orgType',
    'groupCount',
    'participantCount',
    'averageAttendance',
  ];
  sortColumn: any;
  sortOrder: any;
  private intialLoad: boolean = true
  protected readonly clinicStatusLabelMap = statusLabelMap;
  private readonly OnDestroy: Subject<any> = new Subject<any>();
  private clinicListFilters: {
    orgType?: number[],
    status?: number[],
    search?: string,
  } = null;

  constructor(
    private store: Store<fromRoot.State>,
    private commonService: CommonService,
    private clientListService: ClientListService,
    private router: Router,
  ) {
    this.fetchClinicList();

    this.store.select(clinicsList)
      .pipe(takeUntil(this.OnDestroy))
      .subscribe(data => {
        this.loading = data.loading;
        this.clinicListDetails = data;

        if (!data.loading) {
          this.dataSource.data = [...data.list];

          if (data.hasNext) {
            setTimeout(() => {
              if (this.sortColumn && data.pageNumber === 1) {
                this.commonService.scrollColumnInView(this.sortColumn);
              }
            }, 0)

            setTimeout(() => {
              // check window height for infinite scroll
              this.commonService.checkInfiniteScrollHeightAndCallNext('table-wrapper', () => {
                this.onScroll();
              });
            }, 0)
          }
        }

      });

    // listen for filter changes from the filter component
    this.clientListService.onClinicFilterChange.pipe(takeUntil(this.OnDestroy))
      .subscribe((filters) => {
        this.clinicListFilters = filters;
        this.isFirstPage = true;
        this.fetchClinicList();
      });
  }

  fetchClinicList(pageNumber = 1, limit = 10) {
    if (this.intialLoad) {
      this.sortColumn = 'id',
        this.sortOrder = -1;
      this.intialLoad = false;
    }
    this.store.dispatch(FetchClinicListRequest({
      payload: {
        ...this.clinicListFilters,
        pageNumber,
        limit,
        ...(this.sortOrder ? { sortOrder: this.sortOrder } : {}),
        ...(this.sortOrder ? { sortColumn: this.sortColumn } : {}),
      }
    }));
  }

  onScroll() {
    if (this.clinicListDetails?.hasNext && !this.clinicListDetails?.loading) {
      this.fetchClinicList(this.clinicListDetails.pageNumber + 1, this.clinicListDetails.limit);
      this.isFirstPage = false;
    }
  }

  openClinicDetails(clinic: Clinic) {
    if (clinic?.id) {
      this.store.select(drillDownUrls).subscribe(data => {
        const updatedDrillDownUrls = [...data, this.router.url];
        if ([...data].pop() !== this.router.url) {
          this.store.dispatch(SetDrillDownUrls({ payload: updatedDrillDownUrls }));
        }
      }).unsubscribe();

      const urlPrefix = this.router.url.split('/')[1];
      this.router.navigate([`/${urlPrefix}/organization/${clinic.id}/dashboard`]);

    }
  }

  sortData(sort: Sort) {
    this.isFirstPage = true;
    this.sortColumn = `${sort.active}`;
    this.sortOrder = sortOrderMap[sort.direction];
    this.fetchClinicList();
  }

  getOrgList(list: any[]): string {
    return list.map(item => item?.value).join(', ')
  }

  ngOnDestroy() {
    this.OnDestroy.next(null);
    this.OnDestroy.complete();
  }

  protected readonly clientStatusColors = clientStatusColors;
}
